import { invariantResponse } from '@epic-web/invariant'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { motion } from 'framer-motion'
import { ArrowLeftIcon } from 'lucide-react'
import { Link, type LoaderFunctionArgs } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { BlockRenderer } from '#app/components/block-renderer.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { buildMeta, buildNewsArticleJsonLd } from '#app/utils/meta.ts'
import { CONDENSED_FONT_STYLE } from '#app/utils/text-rendering.tsx'
import { type Route } from './+types/_marketing.nieuws.$slug.ts'

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const articles = await prisma.newsArticle.findMany({
			where: { published: true },
			select: { slug: true, updatedAt: true },
		})
		return articles.map((article) => ({
			route: `/nieuws/${article.slug}`,
			lastmod: article.updatedAt?.toISOString(),
			priority: 0.7,
		}))
	}),
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data?.article) {
		return [{ title: 'Artikel niet gevonden' }]
	}
	const title = data.article.metaTitle || data.article.title
	const description = data.article.metaDescription || data.article.excerpt || ''
	return [
		...buildMeta({
			title,
			description,
			url: `/nieuws/${data.article.slug}`,
			image: data.article.featuredImage,
			type: 'article',
			publishedAt: data.article.publishedAt?.toString(),
			author: data.article.author,
		}),
		buildNewsArticleJsonLd({
			title,
			description,
			url: `/nieuws/${data.article.slug}`,
			image: data.article.featuredImage,
			publishedAt: data.article.publishedAt?.toString(),
			modifiedAt: data.article.updatedAt?.toString(),
			author: data.article.author,
		}),
	]
}

export async function loader({ params }: LoaderFunctionArgs) {
	invariantResponse(params.slug, 'Slug is required')

	const article = await prisma.newsArticle.findUnique({
		where: { slug: params.slug, published: true },
	})

	invariantResponse(article, 'Artikel niet gevonden', { status: 404 })

	// Load blocks separately — table may not exist yet on older deployments
	let blocks: Array<{ id: string; newsArticleId: string; type: string; order: number; content: string; createdAt: Date; updatedAt: Date }> = []
	try {
		blocks = await prisma.newsArticleBlock.findMany({
			where: { newsArticleId: article.id },
			orderBy: { order: 'asc' },
		})
	} catch {
		// Table doesn't exist yet — return empty blocks
	}

	const articleWithBlocks = { ...article, blocks }

	// Fetch other published articles for "more news" section
	const moreArticles = await prisma.newsArticle.findMany({
		where: {
			published: true,
			id: { not: article.id },
		},
		select: {
			id: true,
			slug: true,
			title: true,
			excerpt: true,
			featuredImage: true,
			imageAlt: true,
			category: true,
			publishedAt: true,
			heroVideoMode: true,
			heroVideoPlaybackId: true,
		},
		orderBy: { publishedAt: 'desc' },
		take: 3,
	})

	// Fetch services, sectors, case studies for blocks that need them
	const hasBlocks = blocks.length > 0
	const [services, sectors, caseStudies] = hasBlocks
		? await Promise.all([
				prisma.service.findMany({
					select: { id: true, name: true, slug: true, description: true },
					orderBy: { name: 'asc' },
				}),
				prisma.sector.findMany({
					select: { id: true, name: true, slug: true },
					orderBy: { name: 'asc' },
				}),
				prisma.caseStudy.findMany({
					where: { published: true },
					select: {
						id: true,
						title: true,
						slug: true,
						client: true,
						imageUrl: true,
						imageAlt: true,
						featured: true,
						order: true,
						heroVideoMode: true,
						heroVideoPlaybackId: true,
						services: {
							select: { id: true, name: true, slug: true },
						},
						sectors: {
							select: { id: true, name: true, slug: true },
						},
					},
					orderBy: [{ order: 'asc' }, { id: 'asc' }],
				}),
			])
		: [[], [], []]

	return {
		article: articleWithBlocks,
		moreArticles: moreArticles.map((a) => ({
			...a,
			videoMode: (a.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
			videoPlaybackId: a.heroVideoPlaybackId ?? undefined,
		})),
		services,
		sectors,
		caseStudies: caseStudies.map((cs) => ({
			...cs,
			videoMode: (cs.heroVideoMode as 'none' | 'hover' | 'autoplay') ?? 'none',
			videoPlaybackId: cs.heroVideoPlaybackId ?? undefined,
		})),
	}
}

// Layout constants matching block-renderer grid system
const GRID_NARROW = 'max-w-[940px]'
const GRID_WIDE = 'max-w-[1176px]'

const fadeIn = {
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
}

export default function NewsArticlePage({ loaderData }: Route.ComponentProps) {
	const { article, moreArticles, services, sectors, caseStudies } = loaderData
	const hasBlocks = article.blocks && article.blocks.length > 0

	const formattedDate = article.publishedAt
		? new Date(article.publishedAt).toLocaleDateString('nl-NL', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			})
		: null

	return (
		<motion.main
			className="bg-[#f7f1e2]"
			initial="initial"
			animate="animate"
		>
			{/* Hero Section */}
			<section className="bg-[#f7f1e2] px-6 md:px-12 lg:px-24 pt-[calc(8rem+2rem)] pb-8 md:pb-12">
				<div className={`${GRID_NARROW} mx-auto`}>
					{/* Back link */}
					<motion.div variants={fadeIn} className="mb-8">
						<Link
							to="/nieuws"
							className="inline-flex items-center gap-2 text-body-sm text-black/60 hover:text-black transition-colors"
						>
							<ArrowLeftIcon className="w-4 h-4" />
							Alle artikelen
						</Link>
					</motion.div>

					{/* Category + Date */}
					<motion.div
						className="flex flex-wrap items-center gap-3 mb-6"
						variants={fadeIn}
					>
						{article.category && (
							<span className="text-body-xs font-medium text-[#eb008b]">
								{article.category}
							</span>
						)}
						{article.category && formattedDate && (
							<span className="text-black/30">·</span>
						)}
						{formattedDate && (
							<time
								dateTime={article.publishedAt?.toISOString()}
								className="text-body-xs text-black/50"
							>
								{formattedDate}
							</time>
						)}
						{article.author && (
							<>
								<span className="text-black/30">·</span>
								<span className="text-body-xs text-black/50">
									{article.author}
								</span>
							</>
						)}
					</motion.div>

					{/* Title */}
					<motion.h1
						className="text-[40px] leading-none md:text-[56px] lg:text-[76px] text-black mb-6"
						style={CONDENSED_FONT_STYLE}
						variants={fadeIn}
					>
						{article.title}
					</motion.h1>

					{/* Excerpt */}
					{article.excerpt && (
						<motion.p
							className="text-[20px] leading-[1.4] md:text-[24px] text-black/70"
							variants={fadeIn}
						>
							{article.excerpt}
						</motion.p>
					)}
				</div>
			</section>

			{/* Featured Image — wider than text */}
			{article.featuredImage && (
				<motion.section
					className="px-6 md:px-12 lg:px-24 pb-8 md:pb-12"
					initial={{ opacity: 0, y: 18 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.8, delay: 0.2 }}
				>
					<div className={`${GRID_WIDE} mx-auto`}>
						<div className="relative aspect-[16/9] rounded-2xl overflow-hidden">
							<img
								src={article.featuredImage}
								alt={article.imageAlt || article.title}
								className="w-full h-full object-cover"
							/>
						</div>
					</div>
				</motion.section>
			)}

			{/* Article Content — blocks or legacy HTML */}
			{hasBlocks ? (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.7, delay: 0.3 }}
				>
					<BlockRenderer
						blocks={article.blocks}
						services={services}
						sectors={sectors}
						caseStudies={caseStudies}
					/>
				</motion.div>
			) : article.content ? (
				<motion.section
					className="bg-[#f7f1e2] px-6 py-8 md:px-12 md:py-12 lg:px-24 lg:py-16"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.7, delay: 0.3 }}
				>
					<article className={`${GRID_NARROW} mx-auto`}>
						<style>{`
							.article-content h1,
							.article-content h2,
							.article-content h3 {
								font-family: "Helvetica Neue Condensed", "HelveticaNeue-CondensedBlack", "Helvetica Neue", Helvetica, Arial, sans-serif;
								font-stretch: condensed;
								font-weight: 900;
							}
						`}</style>
						<div
							className="article-content max-w-none text-black/80
								[&_h1]:text-[40px] [&_h1]:leading-none [&_h1]:md:text-[56px] [&_h1]:lg:text-[76px] [&_h1]:mt-0 [&_h1]:mb-6 [&_h1]:text-black
								[&_h2]:text-[36px] [&_h2]:leading-none [&_h2]:md:text-[48px] [&_h2]:lg:text-[64px] [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-black
								[&_h3]:text-[28px] [&_h3]:leading-none [&_h3]:md:text-[34px] [&_h3]:lg:text-[40px] [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-black
								[&_h4]:text-[24px] [&_h4]:leading-none [&_h4]:md:text-[28px] [&_h4]:lg:text-[32px] [&_h4]:mt-0 [&_h4]:mb-4 [&_h4]:text-black
								[&_p]:mb-6 [&_p]:text-[20px] [&_p]:leading-[1.6]
								[&_span]:inline
								[&_a]:text-[#eb008b] [&_a]:no-underline hover:[&_a]:underline
								[&_img]:rounded-2xl
								[&_blockquote]:border-l-[#eb008b] [&_blockquote]:bg-[#f7f1e2] [&_blockquote]:py-1 [&_blockquote]:px-4 [&_blockquote]:rounded-r-lg
								[&_strong]:text-black"
							dangerouslySetInnerHTML={{ __html: article.content }}
						/>
					</article>
				</motion.section>
			) : null}

			{/* More Articles */}
			{moreArticles.length > 0 && (
				<motion.section
					className="px-6 py-16 md:px-12 md:py-24 lg:px-24 bg-[#f7f1e2]"
					initial={{ opacity: 0 }}
					whileInView={{ opacity: 1 }}
					viewport={{ once: true }}
				>
					<div className={`${GRID_WIDE} mx-auto`}>
						<h2
							className="text-[36px] leading-none md:text-[48px] lg:text-[64px] text-black mb-10 md:mb-14"
							style={CONDENSED_FONT_STYLE}
						>
							Meer nieuws
						</h2>

						<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
							{moreArticles.map((item) => {
								const itemDate = item.publishedAt
									? new Date(item.publishedAt).toLocaleDateString('nl-NL', {
											year: 'numeric',
											month: 'short',
											day: 'numeric',
										})
									: null

								return (
									<motion.article
										key={item.id}
										className="group"
										whileHover={{ y: -4 }}
										transition={{ duration: 0.2 }}
									>
										<Link
											to={`/nieuws/${item.slug}`}
											className="block"
										>
											<div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-4 bg-[#231a1a]/5">
												{item.featuredImage && (
													<img
														src={item.featuredImage}
														alt={item.imageAlt || item.title}
														className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
													/>
												)}
											</div>

											<div className="flex items-center gap-2 mb-2">
												{item.category && (
													<span className="text-body-xs font-medium text-[#eb008b]">
														{item.category}
													</span>
												)}
												{item.category && itemDate && (
													<span className="text-black/30">·</span>
												)}
												{itemDate && (
													<time className="text-body-2xs text-black/50">
														{itemDate}
													</time>
												)}
											</div>

											<h3 className="text-[20px] leading-[1.2] font-bold text-black group-hover:text-[#eb008b] transition-colors mb-2">
												{item.title}
											</h3>

											{item.excerpt && (
												<p className="text-body-sm text-black/60 line-clamp-2">
													{item.excerpt}
												</p>
											)}
										</Link>
									</motion.article>
								)
							})}
						</div>

						<div className="text-center mt-12">
							<Link
								to="/nieuws"
								className="inline-flex items-center gap-2 px-6 py-3 bg-[#231a1a] text-[#f7f1e2] rounded-lg text-button font-bold hover:bg-[#231a1a]/90 transition-colors"
							>
								Bekijk alle artikelen
							</Link>
						</div>
					</div>
				</motion.section>
			)}
		</motion.main>
	)
}
